import { useState } from 'react';
import { BadgePercent, Clock, Tag, Check, Info, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { promos, services } from '../data/mockData';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function PromosPage() {
  const navigate = useNavigate();
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [expandedPromo, setExpandedPromo] = useState(null);

  const handleApplyPromo = (promo) => {
    setAppliedPromo(promo);
    toast.success(`Promo "${promo.name}" aplicada. Se aplicará en tu próxima reserva.`);
  };

  const handleBookWithPromo = (promo) => {
    setAppliedPromo(promo);
    toast.success(`Promo "${promo.name}" seleccionada`);
    navigate('/');
  };

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-12" data-testid="promos-page">
      {/* Header */}
      <section className="bg-cream py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Badge className="mb-4 bg-copper/10 text-copper-dark border-0 px-4 py-1">
            <Tag className="w-3 h-3 mr-1" />
            Ofertas activas
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-4">
            Promociones
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Aprovecha nuestras ofertas especiales. Descuentos en horas valle, 
            paquetes 2x1 y beneficios exclusivos para nuevos clientes.
          </p>
        </div>
      </section>

      {/* Active promo banner */}
      {appliedPromo && (
        <div className="bg-muted-green/10 border-b border-muted-green/20 py-4 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted-green/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-muted-green" />
              </div>
              <div>
                <p className="font-medium text-sm">Promo activa: {appliedPromo.name}</p>
                <p className="text-xs text-muted-foreground">Se aplicará en tu próxima reserva</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-muted-green hover:bg-muted-green/90 text-cream rounded-full"
              data-testid="go-to-booking-with-promo"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Agendar con promo
            </Button>
          </div>
        </div>
      )}

      {/* Promos Grid */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((promo) => (
              <div
                key={promo.id}
                className={`promo-card p-6 rounded-2xl border bg-white transition-all ${
                  appliedPromo?.id === promo.id
                    ? 'border-muted-green shadow-premium'
                    : 'border-border/50 hover:shadow-premium'
                }`}
                data-testid={`promo-item-${promo.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-copper/10 flex items-center justify-center">
                    <BadgePercent className="w-7 h-7 text-copper" />
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {appliedPromo?.id === promo.id && (
                      <Badge className="badge-available">Aplicada</Badge>
                    )}
                    {promo.limited && (
                      <Badge className="badge-limited">
                        Quedan {promo.remaining}
                      </Badge>
                    )}
                    {promo.newCustomersOnly && (
                      <Badge className="bg-soft-gray text-graphite border-0">
                        Nuevos clientes
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-heading text-2xl text-graphite mb-2">{promo.name}</h3>
                <p className="text-muted-foreground mb-4">{promo.description}</p>

                {/* Discount display */}
                <div className="p-4 bg-copper/5 rounded-xl mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Descuento</span>
                    <span className="font-heading text-3xl text-copper">
                      {promo.discountType === 'fixed' ? 'GRATIS' : `${promo.discount}%`}
                    </span>
                  </div>
                </div>

                {/* Code */}
                <div className="flex items-center justify-between py-3 border-y border-border/50 mb-4">
                  <span className="text-sm text-muted-foreground">Código</span>
                  <code className="font-mono text-sm bg-soft-gray px-3 py-1 rounded">
                    {promo.code}
                  </code>
                </div>

                {/* Conditions */}
                <button
                  onClick={() => setExpandedPromo(expandedPromo === promo.id ? null : promo.id)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-graphite mb-4"
                >
                  <Info className="w-4 h-4" />
                  Ver condiciones
                </button>

                {expandedPromo === promo.id && (
                  <div className="p-4 bg-soft-gray/30 rounded-xl mb-4 animate-fade-in">
                    <p className="text-sm text-muted-foreground">{promo.conditions}</p>
                    {promo.validDays && (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-copper" />
                        <span>
                          Válido: {promo.validDays.join(', ')} {promo.validHours && `(${promo.validHours.start}:00-${promo.validHours.end}:00)`}
                        </span>
                      </div>
                    )}
                    {promo.validServices && (
                      <div className="mt-2 text-sm">
                        <span className="text-muted-foreground">Servicios válidos: </span>
                        {promo.validServices.map(sId => {
                          const service = services.find(s => s.id === sId);
                          return service?.name;
                        }).filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleApplyPromo(promo)}
                    disabled={appliedPromo?.id === promo.id}
                    className="flex-1 rounded-full"
                    data-testid={`apply-promo-btn-${promo.id}`}
                  >
                    {appliedPromo?.id === promo.id ? (
                      <>
                        <Check className="w-4 h-4 mr-1" /> Aplicada
                      </>
                    ) : (
                      'Aplicar'
                    )}
                  </Button>
                  <Button
                    onClick={() => handleBookWithPromo(promo)}
                    className="flex-1 bg-copper hover:bg-copper-dark text-cream rounded-full"
                    data-testid={`book-with-promo-${promo.id}`}
                  >
                    Agendar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 md:py-16 px-4 bg-soft-gray/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-graphite mb-8">
            ¿Cómo funcionan las promos?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                title: 'Elige tu promo',
                description: 'Selecciona la promoción que más te convenga'
              },
              {
                step: '2',
                title: 'Agenda tu cita',
                description: 'El descuento se aplica automáticamente'
              },
              {
                step: '3',
                title: 'Disfruta',
                description: 'Paga el precio con descuento en sucursal'
              }
            ].map((item) => (
              <div key={item.step} className="p-6 bg-white rounded-2xl shadow-premium">
                <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-copper/10 flex items-center justify-center">
                  <span className="font-heading text-lg text-copper">{item.step}</span>
                </div>
                <h3 className="font-medium text-graphite mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-8 px-4 border-t border-border/50">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading text-lg text-graphite mb-4">Términos y condiciones</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Las promociones no son acumulables entre sí.</li>
            <li>• Válidas en todas las sucursales salvo que se indique lo contrario.</li>
            <li>• El descuento se aplica al total antes de impuestos.</li>
            <li>• Las promociones limitadas tienen cupos diarios que se reinician cada día.</li>
            <li>• Nos reservamos el derecho de modificar o cancelar promociones sin previo aviso.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
